import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Grid,
  Chip,
  Avatar,
  Paper,
  Divider,
  IconButton,
  Collapse,
  alpha,
  styled,
  useTheme,
  CircularProgress,
  Badge,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
} from '@mui/material';
import AppPaginationBar from "components/AppPaginationBar";
import {
  Agriculture,
  VideoCall,
  WhatsApp,
  Schedule,
  Person,
  LocationOn,
  Email,
  Phone,
  CalendarToday,
  AccessTime,
  Comment,
  ExpandMore,
  Update as UpdateIcon,
  CheckCircle,
  Pending,
  Link as LinkIcon,
  Search,
  FilterList,
  Clear,
  Refresh,
} from '@mui/icons-material';
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import buttonConfig from "config/ConfigAccessButton";

// Enhanced styled components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.15)}`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
  },
}));

const StatusTabs = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: 10,
  padding: 0,
  backgroundColor: 'transparent',
  borderRadius: 0,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexWrap: 'wrap',
}));

const StatusTab = styled(Button)(({ theme, active }) => ({
  minHeight: 30,
  borderRadius: 0,
  textTransform: 'none',
  fontWeight: 600,
  padding: '6px 12px',
  flex: 'unset',
  fontSize: '0.8125rem',
  minWidth: 170,
  transition: 'all 0.2s ease',
  ...(active ? {
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    '&:hover': {
      backgroundColor: 'transparent',
      color: theme.palette.primary.main,
    },
  } : {
    backgroundColor: 'transparent',
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: 'transparent',
      color: theme.palette.primary.main,
    },
  }),
}));

const ConsultationCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  borderRadius: 16,
  padding: 16,
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.12)}`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    borderRadius: '16px 16px 0 0',
  },
}));

const DetailSection = styled(Paper)(({ theme }) => ({
  padding: 14,
  borderRadius: 12,
  backgroundColor: alpha(theme.palette.background.paper, 0.8),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  backdropFilter: 'blur(10px)',
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        bg: theme.palette.warning.main,
        color: theme.palette.warning.contrastText,
        icon: <Pending sx={{ fontSize: '16px !important' }} />,
      },
      connected: {
        bg: theme.palette.success.main,
        color: theme.palette.success.contrastText,
        icon: <CheckCircle sx={{ fontSize: '16px !important' }} />,
      },
    };
    return configs[status] || configs.pending;
  };

  const config = getStatusConfig(status);
  return {
    backgroundColor: config.bg,
    color: config.color,
    fontWeight: 600,
    fontSize: '0.875rem',
    height: 32,
    borderRadius: 16,
    '& .MuiChip-icon': {
      color: config.color,
    },
  };
});

const PlatformChip = styled(Chip)(({ theme, platform }) => {
  const getPlatformConfig = (platform) => {
    const configs = {
      WhatsApp: {
        bg: '#25D366',
        color: '#ffffff',
        icon: <WhatsApp sx={{ fontSize: '16px !important' }} />,
      },
      Zoom: {
        bg: '#2D8CFF',
        color: '#ffffff',
        icon: <VideoCall sx={{ fontSize: '16px !important' }} />,
      },
      Phone: {
        bg: theme.palette.info.main,
        color: theme.palette.info.contrastText,
        icon: <Phone sx={{ fontSize: '16px !important' }} />,
      },
    };
    return configs[platform] || configs.Phone;
  };

  const config = getPlatformConfig(platform);
  return {
    backgroundColor: config.bg,
    color: config.color,
    fontWeight: 600,
    fontSize: '0.875rem',
    height: 32,
    borderRadius: 16,
    '& .MuiChip-icon': {
      color: config.color,
    },
  };
});

const SearchContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginBottom: 24,
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    backdropFilter: 'blur(10px)',
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    '&:hover': {
      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
    },
    '&.Mui-focused': {
      border: `1px solid ${theme.palette.primary.main}`,
      boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
    },
  },
}));

const TalkToAgronomistList = ({ data, searchQuery, onRefresh, pagination = null, onPageChange, onPageSizeChange, pageSizeOptions = [10, 25, 50, 100] }) => {
  const theme = useTheme();
  
  // Separate state for pending tab
  const [pendingConsultations, setPendingConsultations] = useState([]);
  const [pendingPagination, setPendingPagination] = useState(null);
  const [pendingCurrentPage, setPendingCurrentPage] = useState(1);
  const [pendingPageSize, setPendingPageSize] = useState(10);
  
  // Separate state for connected tab
  const [connectedConsultations, setConnectedConsultations] = useState([]);
  const [connectedPagination, setConnectedPagination] = useState(null);
  const [connectedCurrentPage, setConnectedCurrentPage] = useState(1);
  const [connectedPageSize, setConnectedPageSize] = useState(10);
  
  const [searchInput, setSearchInput] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [updateDialog, setUpdateDialog] = useState({ open: false, consultation: null });
  const [newStatus, setNewStatus] = useState('');
  const [statusCounts, setStatusCounts] = useState({ pending: 0, connected: 0 });
  
  // Track if data has been fetched for each tab
  const pendingDataFetchedRef = useRef(false);
  const connectedDataFetchedRef = useRef(false);
  const statusCountsFetchedRef = useRef(false);

  // Get current tab name based on status filter
  const getCurrentTabName = () => {
    const tabMapping = {
      'pending': 'Pending Button',
      'connected': 'Connected Button'
    };
    return tabMapping[statusFilter] || 'Pending Button';
  };
  
  // Get button permissions based on current tab
  const getCurrentButtonPermissions = () => {
    const currentTab = getCurrentTabName();
    const moduleConfig = buttonConfig["Agronomist"];
    return moduleConfig && moduleConfig[currentTab] ? moduleConfig[currentTab] : {};
  };
  
  // Check if a specific button should be enabled
  const shouldEnableButton = (buttonKey) => {
    const permissions = getCurrentButtonPermissions();
    return permissions[buttonKey] === true;
  };

  // Fetch pending consultations
  const fetchPendingConsultations = async (page = 1, pageSizeParam = pendingPageSize, search = searchValue) => {
    setIsLoading(true);
    try {
      const userId = localStorage.getItem("user_id");
      let url = `${API_ENDPOINTS.GET_talk_to_agronomist}?user_id=${userId}&page=${page}&page_size=${pageSizeParam}&status=pending`;

      // BACKEND SEARCH PARAMETER
      const searchTerm = search !== undefined ? search : searchValue;
      if (searchTerm && searchTerm.trim()) {
        url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }

      const response = await apiService.get(url);
      
      if (response?.success) {
        const fetchedData = Array.isArray(response.data) ? response.data : [];
        setPendingConsultations(fetchedData);
        
        // Properly map pagination data
        const paginationData = response.pagination || {};
        setPendingPagination({
          current_page: paginationData.current_page ?? page,
          page_size: paginationData.page_size ?? pageSizeParam,
          total_items: paginationData.total_items ?? fetchedData.length,
          total_pages: paginationData.total_pages ?? Math.ceil((paginationData.total_items ?? fetchedData.length) / pageSizeParam),
          has_next: paginationData.has_next ?? (page < (paginationData.total_pages ?? 1)),
          has_previous: paginationData.has_previous ?? (page > 1),
          next_page_number: paginationData.next_page_number ?? (page < (paginationData.total_pages ?? 1) ? page + 1 : null),
          previous_page_number: paginationData.previous_page_number ?? (page > 1 ? page - 1 : null)
        });
        setPendingCurrentPage(page);
        pendingDataFetchedRef.current = true;
      }
    } catch (error) {
      console.error("Error fetching pending consultations:", error);
      setPendingConsultations([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch connected consultations
  const fetchConnectedConsultations = async (page = 1, pageSizeParam = connectedPageSize, search = searchValue) => {
    setIsLoading(true);
    try {
      const userId = localStorage.getItem("user_id");
      let url = `${API_ENDPOINTS.GET_talk_to_agronomist}?user_id=${userId}&page=${page}&page_size=${pageSizeParam}&status=connected`;

      // BACKEND SEARCH PARAMETER
      const searchTerm = search !== undefined ? search : searchValue;
      if (searchTerm && searchTerm.trim()) {
        url += `&search=${encodeURIComponent(searchTerm.trim())}`;
      }

      const response = await apiService.get(url);
      
      if (response?.success) {
        const fetchedData = Array.isArray(response.data) ? response.data : [];
        setConnectedConsultations(fetchedData);
        
        // Properly map pagination data
        const paginationData = response.pagination || {};
        setConnectedPagination({
          current_page: paginationData.current_page ?? page,
          page_size: paginationData.page_size ?? pageSizeParam,
          total_items: paginationData.total_items ?? fetchedData.length,
          total_pages: paginationData.total_pages ?? Math.ceil((paginationData.total_items ?? fetchedData.length) / pageSizeParam),
          has_next: paginationData.has_next ?? (page < (paginationData.total_pages ?? 1)),
          has_previous: paginationData.has_previous ?? (page > 1),
          next_page_number: paginationData.next_page_number ?? (page < (paginationData.total_pages ?? 1) ? page + 1 : null),
          previous_page_number: paginationData.previous_page_number ?? (page > 1 ? page - 1 : null)
        });
        setConnectedCurrentPage(page);
        connectedDataFetchedRef.current = true;
      }
    } catch (error) {
      console.error("Error fetching connected consultations:", error);
      setConnectedConsultations([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch status counts for badges
  const fetchStatusCounts = async () => {
    try {
      const userId = localStorage.getItem("user_id");
      if (!userId) return;
      
      const [pendingResponse, connectedResponse] = await Promise.all([
        apiService.get(`${API_ENDPOINTS.GET_talk_to_agronomist}?user_id=${userId}&page=1&page_size=1&status=pending`),
        apiService.get(`${API_ENDPOINTS.GET_talk_to_agronomist}?user_id=${userId}&page=1&page_size=1&status=connected`)
      ]);
      
      setStatusCounts({
        pending: pendingResponse?.pagination?.total_items || 0,
        connected: connectedResponse?.pagination?.total_items || 0,
      });
    } catch (error) {
      console.error("Error fetching status counts:", error);
    }
  };

  // Initial load - fetch status counts
  useEffect(() => {
    if (statusCountsFetchedRef.current) return;
    statusCountsFetchedRef.current = true;
    fetchStatusCounts();
  }, []);

  // Fetch initial data for default tab (pending)
  useEffect(() => {
    if (!pendingDataFetchedRef.current) {
      fetchPendingConsultations();
    }
  }, []);

  // Handle page change for pending
  const handlePendingPageChange = (event, newPage) => {
    if (newPage && newPage !== pendingCurrentPage) {
      fetchPendingConsultations(newPage, pendingPageSize, searchValue);
    }
  };

  // Handle page change for connected
  const handleConnectedPageChange = (event, newPage) => {
    if (newPage && newPage !== connectedCurrentPage) {
      fetchConnectedConsultations(newPage, connectedPageSize, searchValue);
    }
  };

  // Handle page size change for pending
  const handlePendingPageSizeChange = (newPageSize) => {
    setPendingPageSize(newPageSize);
    setPendingCurrentPage(1);
    fetchPendingConsultations(1, newPageSize, searchValue);
  };

  // Handle page size change for connected
  const handleConnectedPageSizeChange = (newPageSize) => {
    setConnectedPageSize(newPageSize);
    setConnectedCurrentPage(1);
    fetchConnectedConsultations(1, newPageSize, searchValue);
  };

  // Handle tab change
  const handleStatusTabChange = (status) => {
    setStatusFilter(status);
    
    // Fetch data for the selected tab if not already fetched
    if (status === 'pending' && !pendingDataFetchedRef.current) {
      fetchPendingConsultations();
    } else if (status === 'connected' && !connectedDataFetchedRef.current) {
      fetchConnectedConsultations();
    }
  };

  const getStatus = (consultation) => {
    if (consultation.connected) return 'connected';
    return 'pending';
  };

  const getStatusDisplay = (consultation) => {
    const status = getStatus(consultation);
    return status === 'connected' ? 'Connected' : 'Pending';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Search handlers
  const handleSearch = () => {
    const searchText = searchInput;
    setSearchValue(searchText);
    setHasSearched(true);
    
    // Reset fetched flags
    pendingDataFetchedRef.current = false;
    connectedDataFetchedRef.current = false;
    
    // Fetch data for current tab with search from backend
    if (statusFilter === 'pending') {
      fetchPendingConsultations(1, pendingPageSize, searchText);
    } else {
      fetchConnectedConsultations(1, connectedPageSize, searchText);
    }
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchValue("");
    setHasSearched(false);
    
    // Reset fetched flags
    pendingDataFetchedRef.current = false;
    connectedDataFetchedRef.current = false;
    
    // Fetch data for current tab without search
    if (statusFilter === 'pending') {
      fetchPendingConsultations(1, pendingPageSize, "");
    } else {
      fetchConnectedConsultations(1, connectedPageSize, "");
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleHeaderRefresh = async () => {
    // Refresh status counts
    await fetchStatusCounts();
    
    // Reset fetched flags for both tabs
    pendingDataFetchedRef.current = false;
    connectedDataFetchedRef.current = false;
    
    // Clear data for both tabs
    setPendingConsultations([]);
    setConnectedConsultations([]);
    
    // Fetch fresh data for current tab
    if (statusFilter === 'pending') {
      fetchPendingConsultations(1, pendingPageSize, searchValue);
    } else {
      fetchConnectedConsultations(1, connectedPageSize, searchValue);
    }
  };

  // Get current consultations based on active tab
  const currentConsultations = statusFilter === 'pending' ? pendingConsultations : connectedConsultations;
  const currentPaginationData = statusFilter === 'pending' ? pendingPagination : connectedPagination;
  const currentPageChangeHandler = statusFilter === 'pending' ? handlePendingPageChange : handleConnectedPageChange;

  // NO FRONTEND FILTERING - BACKEND SEARCH ONLY
  const filteredConsultations = currentConsultations;

  const toggleExpanded = (id) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedCards(newExpanded);
  };

  const handleUpdateStatus = (consultation) => {
    setUpdateDialog({ open: true, consultation });
    setNewStatus(getStatus(consultation));
  };

  const handleStatusUpdate = async (consultation) => {
    try {
      const userId = localStorage.getItem("user_id");
      
      const payload = {
        ide: consultation.id,
        connected: newStatus === "connected",
        user_id: userId,
      };
      const response = await apiService.put(
        `${API_ENDPOINTS.UPDATE_consultation_status}`,
        payload
      );
      if (response?.success) {
        // Refresh status counts
        await fetchStatusCounts();
        
        // Reset fetched flags for both tabs since data has changed
        pendingDataFetchedRef.current = false;
        connectedDataFetchedRef.current = false;
        
        // Clear both tab data
        setPendingConsultations([]);
        setConnectedConsultations([]);
        
        // Refresh current tab data
        if (statusFilter === 'pending') {
          await fetchPendingConsultations(pendingCurrentPage, pendingPageSize, searchValue);
        } else {
          await fetchConnectedConsultations(connectedCurrentPage, connectedPageSize, searchValue);
        }
        
        setNewStatus(getStatus(consultation));
      }
    } catch (error) {
      console.error("Error updating order status:", error);
    } finally {
      setUpdateDialog({ open: false, consultation: null });
    }
  };

  const ConsultationCardComponent = ({ consultation }) => {
    const isExpanded = expandedCards.has(consultation.id);
    const status = getStatus(consultation);

    return (
      <ConsultationCard>
        <Box onClick={() => toggleExpanded(consultation.id)}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: theme.palette.primary.main,
                  fontSize: '1.2rem',
                  fontWeight: 600,
                }}
              >
                {consultation.user?.first_name?.[0]}{consultation.user?.last_name?.[0]}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={600} color="text.primary">
                  {consultation.user?.first_name} {consultation.user?.last_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {consultation.user?.village}, {consultation.user?.district}
                </Typography>
              </Box>
            </Box>
            <IconButton
              sx={{
                transition: 'transform 0.3s ease',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              <ExpandMore />
            </IconButton>
          </Box>

          <Grid container spacing={1} alignItems="center" mb={1}>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Topic
                </Typography>
                <Chip
                  label={consultation.topic}
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    color: theme.palette.info.main,
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Platform
                </Typography>
                <PlatformChip
                  label={consultation.platform}
                  platform={consultation.platform}
                  size="small"
                  icon={consultation.platform === 'WhatsApp' ? <WhatsApp /> : <VideoCall />}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Status
                </Typography>
                <StatusChip
                  label={getStatusDisplay(consultation)}
                  status={status}
                  size="small"
                  icon={status === 'connected' ? <CheckCircle /> : <Pending />}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Scheduled
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatDate(consultation.date)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatTime(consultation.time)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <DetailSection elevation={0}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Person color="primary" />
                  <Typography variant="h6" fontWeight={600}>
                    Contact Information
                  </Typography>
                </Box>
                <Stack spacing={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Email sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2">{consultation.user?.email || 'N/A'}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Phone sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2">{consultation.user?.mobile_number}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={2}>
                    <LocationOn sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="body2">
                      {consultation.user?.village}, {consultation.user?.district}, {consultation.user?.state} - {consultation.user?.pincode}
                    </Typography>
                  </Box>
                </Stack>
              </DetailSection>
            </Grid>

            <Grid item xs={12} md={6}>
              <DetailSection elevation={0}>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  <Agriculture color="success" />
                  <Typography variant="h6" fontWeight={600}>
                    Consultation Details
                  </Typography>
                </Box>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Expert Type
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {consultation.expert}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Created At
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatDate(consultation.created_at)}
                    </Typography>
                  </Box>
                </Stack>
              </DetailSection>
            </Grid>

            {consultation.comments && (
              <Grid item xs={12}>
                <DetailSection elevation={0}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Comment color="info" />
                    <Typography variant="h6" fontWeight={600}>
                      Comments
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      borderRadius: 2,
                      border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                      fontStyle: 'italic',
                    }}
                  >
                    {consultation.comments}
                  </Typography>
                </DetailSection>
              </Grid>
            )}

            <Grid item xs={12}>
              <DetailSection elevation={0}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="h6" fontWeight={600}>
                    Actions
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="contained"
                      startIcon={<UpdateIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpdateStatus(consultation);
                      }}
                      disabled={statusFilter === 'connected' || !shouldEnableButton('update_status')}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 3,
                        opacity: (statusFilter === 'connected' || !shouldEnableButton('update_status')) ? 0.6 : 1
                      }}
                    >
                      Update Status
                    </Button>
                  </Stack>
                </Box>
              </DetailSection>
            </Grid>
          </Grid>
        </Collapse>
      </ConsultationCard>
    );
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 2 }, maxWidth: '100%', mx: 'auto' }}>
      {/* Status Tabs */}
      <StatusTabs mb={1.25}>
        <StatusTab
          active={statusFilter === 'pending'}
          onClick={() => handleStatusTabChange('pending')}
          startIcon={<Pending />}
        >
          Pending ({statusCounts.pending})
        </StatusTab>
        <StatusTab
          active={statusFilter === 'connected'}
          onClick={() => handleStatusTabChange('connected')}
          startIcon={<CheckCircle />}
        >
          Connected ({statusCounts.connected})
        </StatusTab>
      </StatusTabs>

      {/* Search and Rows Dropdown */}
      <StyledCard sx={{ p: 1.25, mb: 1.25, borderRadius: 2 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
              <SearchContainer sx={{ flex: 1, minWidth: { xs: '100%', md: 320 }, mb: 0 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by farmer name, topic, platform, or expert type..."
                  value={searchInput}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setSearchInput(newValue);
                    if (hasSearched && newValue !== searchValue) {
                      setHasSearched(false);
                    }
                    // Auto refresh when search text is completely cleared
                    if (newValue === "" && searchValue !== "") {
                      setSearchValue("");
                      setHasSearched(false);
                      // Fetch data for current tab without search
                      if (statusFilter === 'pending') {
                        fetchPendingConsultations(1, pendingPageSize, "");
                      } else {
                        fetchConnectedConsultations(1, connectedPageSize, "");
                      }
                    }
                  }}
                  onKeyPress={handleKeyPress}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: searchInput && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleClearSearch} edge="end">
                          <Clear fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: { height: 36 }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: 36,
                      borderRadius: 2
                    }
                  }}
                />
              </SearchContainer>
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<Search />}
                disabled={!searchInput.trim() || (searchInput === searchValue && hasSearched)}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: "none", 
                  height: 36,
                  minWidth: 130
                }}
              >
                Search
              </Button>
              
              <FormControl size="small" sx={{ minWidth: 100, ml: { xs: 0, md: 'auto' } }}>
                <InputLabel>Rows</InputLabel>
                <Select 
                  value={statusFilter === 'pending' ? pendingPageSize : connectedPageSize}
                  label="Rows" 
                  onChange={(e) => {
                    if (statusFilter === 'pending') {
                      handlePendingPageSizeChange(e.target.value);
                    } else {
                      handleConnectedPageSizeChange(e.target.value);
                    }
                  }}
                >
                  {pageSizeOptions.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
        </Grid>
      </StyledCard>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 2, flexWrap: 'wrap', gap: 2 }}>
      </Box>

      {/* Consultations List */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress size={40} />
        </Box>
      ) : filteredConsultations.length === 0 ? (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Agriculture sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No consultations found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search or filter criteria
          </Typography>
        </Paper>
      ) : (
        <>
          <Stack spacing={3}>
            {filteredConsultations.map((consultation) => (
              <ConsultationCardComponent key={consultation.id} consultation={consultation} />
            ))}
          </Stack>
          <AppPaginationBar 
            pagination={currentPaginationData} 
            onPageChange={currentPageChangeHandler} 
            itemLabel="consultations" 
          />
        </>
      )}

      {/* Update Status Dialog */}
      <Dialog open={updateDialog.open} onClose={() => setUpdateDialog({ open: false, consultation: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Update Consultation Status</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              label="Status"
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="connected">Connected</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateDialog({ open: false, consultation: null })}>
            Cancel
          </Button>
          <Button onClick={() => handleStatusUpdate(updateDialog.consultation)} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TalkToAgronomistList;  